export const TechniqueStatus = {
  Unchecked: 'unchecked',
  MarkedForPractice: 'marked',
  Completed: 'completed',
  // Favorites the user is "done with": hidden from the Library and Favorites,
  // shown only in the Archived view (un-archive returns them to Favorites).
  Archived: 'archived',
} as const;

export type TechniqueStatus = (typeof TechniqueStatus)[keyof typeof TechniqueStatus];

export const BeltRank = {
  WhiteBelt: 1,
  BlueBelt: 2,
  PurpleBelt: 3,
  BrownBelt: 4,
  BlackBelt: 5,
} as const;

export type BeltRank = (typeof BeltRank)[keyof typeof BeltRank];


export const TechniqueType = {
  Submission: 'submission',
  Sweep: 'sweep',
  Pass: 'pass',
  Takedown: 'takedown',
  Escape: 'escape',
  Counter: 'counter',
  Transition: 'transition',
  Drill: 'drill',
  Concept: 'concept',
} as const;

export type TechniqueType = (typeof TechniqueType)[keyof typeof TechniqueType];

export const Uniform = {
  Gi: 'gi',
  NoGi: 'nogi',
} as const;

export type Uniform = (typeof Uniform)[keyof typeof Uniform];

export const Art = {
  Bjj: 'bjj',
  Judo: 'judo',
  Wrestling: 'wrestling',
  Sambo: 'sambo',
} as const;

export type Art = (typeof Art)[keyof typeof Art];

export interface Category {
  id: string;
  label: string;
  color: string;
  /** Arts this position applies to. Undefined = BJJ-only (all current data). */
  arts?: Art[];
}

export interface Technique {
  id: string;
  name: string;
  japaneseName?: string;
  /** The move's other names — what other arts or gyms call it. */
  aliases?: string[];
  categoryId: string;
  tier: BeltRank;
  type: TechniqueType;
  /** Gi / No-Gi applicability. Undefined is treated as Gi (legacy data). */
  uniform?: Uniform;
  /** Grappling arts this technique applies to. Undefined = ['bjj'] (legacy data). */
  arts?: Art[];
  instagramUrl?: string;
  /** Grid-view thumbnail. The source page's og:image, copied into Firebase
      Storage once on submission so the URL never expires (the CDN links the
      video cache holds do). Undefined for YouTube, whose thumbnail is derived
      from the video id, and for anything scraped before this existed. */
  thumbnail?: string;
  /** Poster handle (e.g. "danaher_bjj") — scraped from Instagram/TikTok on
      submission, or set manually by an admin. Renders as an @-chip. */
  author?: string;
  /** Who the technique is credited to (e.g. "Lachlan Giles") when that isn't
      the poster. Manually set by an admin; shown on the card over `author`. */
  attribution?: string;
  /** The entry or situation this particular clip teaches the move from, e.g.
      "From Failed Arm Drag". Purely a label: it does NOT split the technique
      into a separate entry, so every clip of the same move stacks under one
      card and the context tells the versions apart inside the picker. */
  context?: string;
  /** Sport rules: the id of the move in the `rules` table this technique
      falls under, when set by hand. Unset, it matches by name. See
      utils/legality.ts. */
  rule?: string;
  /** When the clip entered the library (ms since epoch). Seeded clips all
      share the seed date; user submissions get their approval time. */
  createdAt?: number;
}

export interface TechniqueEdge {
  source: string;
  target: string;
}

export type StatusMap = Record<string, TechniqueStatus>;

/** A user-made collection of favorited techniques (stored per-device). */
export interface Collection {
  id: string;
  name: string;
  techniqueIds: string[];
}
